import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check, CheckCheck, ChevronLeft, ChevronRight, CircleAlert, ExternalLink, Loader2, Pencil, RefreshCw, Search, X,
} from 'lucide-react';
import { useNavigation } from '../../context/NavigationContext';
import {
  apiError, approveClassified, deleteMapping, getClassificationSummary, getClassifyRun, getDefinitions, getMappings,
  getTaxonomy, getUnmapped, listClassified, saveClassification, saveDefinition, saveMapping, startClassifyRun,
} from '../../api/catalogue';
import { CategoryLine, ReasonList, ReviewPill } from './ClassificationView';
import { CATALOGUE_CSS, Notice, PageHeader, PrimaryButton, SecondaryButton, Thumb, ago, focusRing, num } from './shared';

// Catalogue -> Attributes: everything the classifier could not settle on its own,
// done here instead of by hand outside ZOCS.
//   Review        designs to check; edit the category, sub-categories, occasions and
//                 attributes, or approve what the classifier chose. Approved rows are
//                 never overwritten by a re-run.
//   Unmapped      raw words from the pages/tags that matched no value: map each to a
//                 value or ignore it. Applies on the next run.
//   Definitions   one line per sub-category so tagging stays consistent.

const PAGE = 30;
const REVIEW_OPTIONS = [['needs_review', 'Needs review'], ['ok', 'Mapped'], ['approved', 'Approved'], ['all', 'All']];

function useTaxonomy() {
  const [tax, setTax] = useState(null);
  useEffect(() => { getTaxonomy().then(setTax).catch(() => setTax(null)); }, []);
  return tax;
}

function Tile({ label, value, tone, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`text-left rounded-xl border px-4 py-3 bg-white transition ${active ? 'border-zoddle-green ring-1 ring-zoddle-green/30' : 'border-gray-200 hover:border-gray-300'} ${focusRing}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
      <p className={`mt-0.5 text-[22px] font-semibold tabular-nums ${tone}`}>{num(value)}</p>
    </button>
  );
}

function Pager({ total, page, setPage }) {
  const pages = Math.max(1, Math.ceil(total / PAGE));
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 text-[12.5px] text-gray-500">
      <span>{num(total)} design{total === 1 ? '' : 's'}</span>
      <span className="flex items-center gap-1.5">
        <button disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Previous page"
          className={`h-8 w-8 rounded-lg border border-gray-200 flex items-center justify-center disabled:opacity-30 hover:bg-gray-50 ${focusRing}`}><ChevronLeft size={15} /></button>
        <span className="tabular-nums px-1">{page + 1} / {pages}</span>
        <button disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} aria-label="Next page"
          className={`h-8 w-8 rounded-lg border border-gray-200 flex items-center justify-center disabled:opacity-30 hover:bg-gray-50 ${focusRing}`}><ChevronRight size={15} /></button>
      </span>
    </div>
  );
}

// ── editor ────────────────────────────────────────────────────────────────
function Toggle({ on, onClick, children, extra, title }) {
  return (
    <button type="button" onClick={onClick} title={title} aria-pressed={on}
      className={`inline-flex items-center h-7 px-2.5 rounded-md text-[12.5px] ring-1 ring-inset transition ${on
        ? (extra ? 'bg-violet-600 text-white ring-violet-600' : 'bg-zoddle-green text-white ring-zoddle-green')
        : (extra ? 'bg-violet-50 text-violet-700 ring-violet-200 hover:bg-violet-100' : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50')} ${focusRing}`}>
      {on && <Check size={12} className="mr-1" aria-hidden />}{children}
    </button>
  );
}

function Editor({ design, tax, defs, onClose, onSaved }) {
  const [category, setCategory] = useState(design.category || '');
  const [subs, setSubs] = useState(design.subcategories || []);
  const [occs, setOccs] = useState(design.occasions || []);
  const [attrs, setAttrs] = useState(() => {
    const o = {};
    (design.attributes ? Object.entries(design.attributes) : []).forEach(([k, v]) => { o[k] = v; });
    return o;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const flip = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const pickCategory = (c) => {
    setCategory(c);
    setSubs((cur) => cur.filter((s) => (tax.categories[c] || []).includes(s)));
    setAttrs((cur) => Object.fromEntries(Object.entries(cur).filter(([k]) => tax.attributes[k]?.applies_to.includes(c))));
  };
  const applicable = Object.entries(tax.attributes).filter(([, spec]) => spec.applies_to.includes(category));

  async function save() {
    setSaving(true); setError('');
    try {
      await saveClassification(design.design_id, {
        category, subcategories: subs, occasions: occs,
        attributes: Object.fromEntries(Object.entries(attrs).filter(([, v]) => v.length)),
      });
      onSaved();
    } catch (e) { setError(apiError(e)); } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" aria-label="Edit classification">
      <div className="absolute inset-0 bg-gray-900/20 catalogue-fade" onClick={onClose} />
      <aside className="relative h-full w-full sm:w-[600px] bg-white shadow-2xl border-l border-gray-200 flex flex-col catalogue-drawer">
        <header className="px-5 py-4 flex items-start gap-3 border-b border-gray-100">
          <Thumb url={design.image_url} size="h-12 w-12" />
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-semibold text-gray-900 leading-snug">
              <span className="font-mono text-gray-400 mr-1.5">{design.design_code}</span>{design.design_name}
            </p>
            <p className="mt-0.5 text-[12.5px] text-gray-500">
              {design.brand} · ZOCS {design.zocs_category}, {design.gender}{design.color ? `, ${design.color}` : ''}
            </p>
            {design.title && <p className="mt-1 text-[12.5px] text-gray-700">Page: {design.title}
              {design.product_link && <a href={design.product_link} target="_blank" rel="noreferrer" className="ml-2 text-zoddle-blue hover:underline inline-flex items-center gap-0.5">open <ExternalLink size={11} /></a>}</p>}
          </div>
          <button onClick={onClose} aria-label="Close" className={`h-8 w-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 ${focusRing}`}><X size={17} /></button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {design.reasons.length > 0 && <ReasonList reasons={design.reasons} />}
          {error && <Notice icon={CircleAlert}>{error}</Notice>}

          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">Catalog Category (one)</h4>
            <select value={category} onChange={(e) => pickCategory(e.target.value)}
              className={`h-9 w-full rounded-lg border border-gray-200 px-3 text-[13px] bg-white ${focusRing}`}>
              <option value="">Pick a category…</option>
              {Object.keys(tax.categories).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {category && (
            <>
              <div>
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">Sub-categories (one or more)</h4>
                <div className="flex flex-wrap gap-1.5">
                  {(tax.categories[category] || []).map((s) => (
                    <Toggle key={s} on={subs.includes(s)} onClick={() => flip(subs, setSubs, s)} title={defs[s]}>{s}</Toggle>
                  ))}
                </div>
                {subs.some((s) => defs[s]) && (
                  <ul className="mt-2 space-y-0.5 text-[11.5px] text-gray-500">
                    {subs.filter((s) => defs[s]).map((s) => <li key={s}><b className="font-medium text-gray-700">{s}</b>: {defs[s]}</li>)}
                  </ul>
                )}
              </div>
              <div>
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">Occasions (one or more)</h4>
                <div className="flex flex-wrap gap-1.5">
                  {tax.occasions.map((o) => <Toggle key={o} on={occs.includes(o)} onClick={() => flip(occs, setOccs, o)}>{o}</Toggle>)}
                </div>
              </div>
              {applicable.map(([key, spec]) => (
                <div key={key}>
                  <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">{spec.label}</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {spec.values.map((v) => (
                      <Toggle key={v} extra={(spec.extra || []).includes(v)} on={(attrs[key] || []).includes(v)}
                        onClick={() => setAttrs({ ...attrs, [key]: (attrs[key] || []).includes(v) ? attrs[key].filter((x) => x !== v) : [...(attrs[key] || []), v] })}>{v}</Toggle>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-[11.5px] text-gray-400"><span className="inline-block h-2 w-2 rounded-sm bg-violet-300 mr-1" />Violet values are extra to the agreed taxonomy, kept as real data.</p>
            </>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-3">
          <p className="text-[12px] text-gray-400">Saved to every size of this design and marked Approved.</p>
          <div className="flex gap-2">
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton onClick={save} disabled={saving || !category}>
              {saving && <Loader2 size={14} className="motion-safe:animate-spin" />}Save &amp; approve
            </PrimaryButton>
          </div>
        </footer>
      </aside>
    </div>
  );
}

// ── review tab ────────────────────────────────────────────────────────────
function ReviewTab({ summary, tax, defs, refreshSummary, initialDesignId, filterFromTile, setFilterFromTile }) {
  const [review, setReview] = useState('needs_review');
  const [reason, setReason] = useState('');
  const [category, setCategory] = useState('');
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [list, setList] = useState(null);
  const [picked, setPicked] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const opened = useRef(false);

  useEffect(() => { if (filterFromTile) { setReview(filterFromTile); setPage(0); setFilterFromTile(null); } }, [filterFromTile, setFilterFromTile]);
  useEffect(() => { const t = setTimeout(() => { setQ(query.trim()); setPage(0); }, 300); return () => clearTimeout(t); }, [query]);

  const load = useCallback(async () => {
    try {
      setError('');
      setList(await listClassified({ review, reason: reason || undefined, category: category || undefined, q: q || undefined, offset: page * PAGE, limit: PAGE }));
    } catch (e) { setError(apiError(e)); }
  }, [review, reason, category, q, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {      // arriving from Full catalogue: open that design's editor
    if (!initialDesignId || opened.current) return;
    opened.current = true;
    listClassified({ review: 'all', design_id: initialDesignId, limit: 1 }).then((r) => r.designs[0] && setEditing(r.designs[0])).catch(() => {});
  }, [initialDesignId]);

  const after = () => { setEditing(null); setPicked([]); load(); refreshSummary(); };
  async function approve(ids) {
    setMsg(''); setError('');
    try {
      const r = await approveClassified(ids);
      const skipped = Object.keys(r.skipped).length;
      setMsg(`${r.approved} approved${skipped ? `, ${skipped} skipped because they are not complete: open them to fix` : ''}.`);
      setPicked([]); load(); refreshSummary();
    } catch (e) { setError(apiError(e)); }
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 items-center">
        <select value={review} onChange={(e) => { setReview(e.target.value); setPage(0); }} aria-label="State"
          className={`h-9 rounded-lg border border-gray-200 px-2.5 text-[13px] bg-white ${focusRing}`}>
          {REVIEW_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={reason} onChange={(e) => { setReason(e.target.value); setPage(0); }} aria-label="Reason"
          className={`h-9 rounded-lg border border-gray-200 px-2.5 text-[13px] bg-white ${focusRing}`}>
          <option value="">Any reason</option>
          {Object.entries({ no_subcategory: 'No sub-category', disagree: 'Page and ZOCS disagree', no_occasion: 'No occasion', ethnic_gender: 'Ethnic: girls or boys', no_colour: 'No colour', no_category: 'No category' })
            .map(([k, l]) => <option key={k} value={k}>{l}{summary?.reasons?.[k] ? ` (${summary.reasons[k]})` : ''}</option>)}
        </select>
        <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(0); }} aria-label="Category"
          className={`h-9 rounded-lg border border-gray-200 px-2.5 text-[13px] bg-white ${focusRing}`}>
          <option value="">Any category</option>
          {tax && Object.keys(tax.categories).map((c) => <option key={c} value={c}>{c}</option>)}
          <option value="(none)">(no category)</option>
        </select>
        <label className="flex-1 min-w-[180px] flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-200 bg-white focus-within:ring-2 focus-within:ring-zoddle-green/30">
          <Search size={14} className="text-gray-400" aria-hidden />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Design code, name or brand"
            className="flex-1 min-w-0 text-[13px] outline-none bg-transparent" />
        </label>
        <SecondaryButton disabled={!picked.length} onClick={() => approve(picked)}>
          <CheckCheck size={14} /> Approve {picked.length || ''} selected
        </SecondaryButton>
      </div>
      {msg && <Notice tone="green" className="mt-3">{msg}</Notice>}
      {error && <Notice icon={CircleAlert} className="mt-3">{error}</Notice>}

      <div className="mt-3 rounded-xl border border-gray-200 bg-white overflow-hidden">
        {!list && !error && <p className="p-6 text-[13px] text-gray-400">Loading…</p>}
        {list && list.designs.length === 0 && <p className="p-8 text-center text-[13px] text-gray-400">Nothing here.</p>}
        {list && list.designs.map((d) => (
          <div key={d.design_id} className="flex gap-3 px-4 py-3 border-t first:border-t-0 border-gray-100">
            <input type="checkbox" aria-label={`Select ${d.design_code}`} className="mt-1 h-4 w-4 accent-zoddle-green"
              checked={picked.includes(d.design_id)}
              onChange={(e) => setPicked(e.target.checked ? [...picked, d.design_id] : picked.filter((x) => x !== d.design_id))} />
            <Thumb url={d.image_url} size="h-14 w-14" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-[13px] font-medium text-gray-900 truncate">
                  <span className="font-mono text-gray-400 mr-1.5">{d.design_code}</span>{d.design_name}
                </p>
                <ReviewPill review={d.review} label={REVIEW_OPTIONS.find(([k]) => k === d.review)?.[1]} />
              </div>
              <p className="text-[12px] text-gray-500 truncate">{d.brand} · ZOCS {d.zocs_category}, {d.gender}{d.site ? ` · ${d.site}` : ''}{d.title ? ` · “${d.title}”` : ''}</p>
              <CategoryLine catalog={{ category: d.category, subcategories: d.subcategories, review: d.review }} className="mt-0.5" />
              {d.reasons.length > 0 && <ul className="mt-1 text-[12px] text-amber-700 list-disc pl-4">{d.reasons.map((r) => <li key={r}>{r}</li>)}</ul>}
            </div>
            <div className="flex items-start gap-2 shrink-0">
              {d.review !== 'approved' && d.category && d.reasons.length === 0 && (
                <SecondaryButton onClick={() => approve([d.design_id])}><Check size={13} /> Approve</SecondaryButton>
              )}
              <SecondaryButton onClick={() => setEditing(d)}><Pencil size={13} /> Edit</SecondaryButton>
            </div>
          </div>
        ))}
        {list && <Pager total={list.total} page={page} setPage={setPage} />}
      </div>

      {editing && tax && <Editor design={editing} tax={tax} defs={defs} onClose={() => setEditing(null)} onSaved={after} />}
    </div>
  );
}

// ── unmapped words ────────────────────────────────────────────────────────
function UnmappedRow({ item, fields, onSaved }) {
  const [target, setTarget] = useState(item.choice?.action === 'map' ? item.choice.target : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function choose(action) {
    setBusy(true); setError('');
    try {
      await saveMapping({ field: item.field, raw_value: item.raw_value, action, target_value: action === 'map' ? target : null });
      onSaved();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  }
  return (
    <tr className="border-t border-gray-100 align-top">
      <td className="px-4 py-2.5 text-[12.5px] text-gray-500 capitalize whitespace-nowrap">{item.field.replace('_', ' ')}</td>
      <td className="px-2 py-2.5 text-[13px] text-gray-900">{item.raw_value}
        <p className="text-[11px] text-gray-400">{item.sources.join(', ')}</p></td>
      <td className="px-2 py-2.5 text-[12.5px] tabular-nums text-gray-600">{num(item.designs)}</td>
      <td className="px-2 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <select value={target} onChange={(e) => setTarget(e.target.value)} aria-label={`Map ${item.raw_value} to`}
            className={`h-8 rounded-lg border border-gray-200 px-2 text-[12.5px] bg-white ${focusRing}`}>
            <option value="">Map to…</option>
            {(fields[item.field] || []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <SecondaryButton className="!h-8" disabled={!target || busy} onClick={() => choose('map')}>Map</SecondaryButton>
          <SecondaryButton className="!h-8" disabled={busy} onClick={() => choose('ignore')}>Ignore</SecondaryButton>
          {item.choice && <span className="text-[11.5px] text-green-700">{item.choice.action === 'ignore' ? 'ignoring' : `→ ${item.choice.target}`} (next run)</span>}
        </div>
        {error && <p className="mt-1 text-[12px] text-zoddle-pink">{error}</p>}
      </td>
    </tr>
  );
}

function UnmappedTab({ onRun, running }) {
  const [data, setData] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [field, setField] = useState('');
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try { setError(''); const [u, m] = await Promise.all([getUnmapped(), getMappings()]); setData(u); setMappings(m); }
    catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const items = (data?.items || []).filter((i) => !field || i.field === field);
  const fieldsPresent = useMemo(() => [...new Set((data?.items || []).map((i) => i.field))], [data]);
  return (
    <div>
      <Notice tone="gray" className="mb-3">
        Words from product pages and ZOCS tags that matched no value. Map one to a value, or ignore it; the classifier uses your choice on its next run.
        <button onClick={onRun} disabled={running} className={`ml-2 font-medium text-zoddle-blue hover:underline disabled:opacity-40 ${focusRing} rounded`}>Re-run now</button>
      </Notice>
      {error && <Notice icon={CircleAlert} className="mb-3">{error}</Notice>}
      <div className="mb-3 flex items-center gap-2">
        <select value={field} onChange={(e) => setField(e.target.value)} aria-label="Attribute"
          className={`h-9 rounded-lg border border-gray-200 px-2.5 text-[13px] bg-white ${focusRing}`}>
          <option value="">All attributes</option>
          {fieldsPresent.map((f) => <option key={f} value={f}>{f.replace('_', ' ')}</option>)}
        </select>
        <span className="text-[12.5px] text-gray-400">{items.length} word{items.length === 1 ? '' : 's'}</span>
      </div>
      <div className="rounded-xl border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gray-400">
            <th className="px-4 py-2 font-semibold">Attribute</th><th className="px-2 py-2 font-semibold">Word</th>
            <th className="px-2 py-2 font-semibold">Designs</th><th className="px-2 py-2 font-semibold">Choice</th>
          </tr></thead>
          <tbody>
            {!data && <tr><td colSpan={4} className="p-6 text-[13px] text-gray-400">Loading…</td></tr>}
            {data && items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[13px] text-gray-400">No unmapped words. Run the classifier to refresh this list.</td></tr>}
            {items.map((i) => <UnmappedRow key={`${i.field}|${i.key}`} item={i} fields={data.fields} onSaved={load} />)}
          </tbody>
        </table>
      </div>
      {mappings.length > 0 && (
        <section className="mt-6">
          <h3 className="text-[13px] font-semibold text-gray-900 mb-2">Saved choices · {mappings.length}</h3>
          <ul className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
            {mappings.map((m) => (
              <li key={m.mapping_id} className="flex items-center gap-3 px-4 py-2 text-[12.5px]">
                <span className="capitalize text-gray-500 w-28 shrink-0">{m.field.replace('_', ' ')}</span>
                <span className="text-gray-900 flex-1 min-w-0 truncate">“{m.raw_value}” {m.action === 'ignore' ? 'is ignored' : <>maps to <b>{m.target_value}</b></>}</span>
                <button onClick={async () => { await deleteMapping(m.mapping_id); load(); }} aria-label={`Forget ${m.raw_value}`}
                  className={`h-7 w-7 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 flex items-center justify-center ${focusRing}`}><X size={14} /></button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ── definitions ───────────────────────────────────────────────────────────
function DefinitionRow({ row, onSaved }) {
  const [text, setText] = useState(row.definition);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => setText(row.definition), [row.definition]);
  const save = async (value) => {
    setBusy(true); setError('');
    try { await saveDefinition(row.subcategory, value); onSaved(); } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[200px_minmax(0,1fr)_auto] gap-2 sm:gap-3 items-start px-4 py-2.5 border-t border-gray-100">
      <p className="text-[13px] font-medium text-gray-900 pt-1.5">{row.subcategory}
        {row.custom && <span className="ml-1.5 text-[10.5px] font-medium text-sky-700 bg-sky-50 ring-1 ring-inset ring-sky-200 rounded px-1">edited</span>}</p>
      <div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} aria-label={`Definition of ${row.subcategory}`}
          className={`w-full rounded-lg border border-gray-200 px-3 py-1.5 text-[13px] ${focusRing}`} />
        {error && <p className="text-[12px] text-zoddle-pink">{error}</p>}
      </div>
      <div className="flex gap-1.5">
        <SecondaryButton className="!h-8" disabled={busy || text === row.definition} onClick={() => save(text)}>Save</SecondaryButton>
        {row.custom && <SecondaryButton className="!h-8" disabled={busy} onClick={() => save('')}>Reset</SecondaryButton>}
      </div>
    </div>
  );
}

function DefinitionsTab({ rows, reload }) {
  const groups = useMemo(() => {
    const m = new Map();
    (rows || []).forEach((r) => m.set(r.category, [...(m.get(r.category) || []), r]));
    return [...m.entries()];
  }, [rows]);
  if (!rows) return <p className="text-[13px] text-gray-400">Loading…</p>;
  return (
    <div className="space-y-4">
      <Notice tone="gray">One line per sub-category, so everyone tags the same way. These are drafts written from the data; edit any of them. They show in the editor next to each sub-category.</Notice>
      {groups.map(([cat, list]) => (
        <section key={cat} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <h3 className="px-4 py-2.5 text-[13px] font-semibold text-gray-900 bg-gray-50">{cat}</h3>
          {list.map((r) => <DefinitionRow key={r.subcategory} row={r} onSaved={reload} />)}
        </section>
      ))}
    </div>
  );
}

// ── page ──────────────────────────────────────────────────────────────────
export default function Attributes() {
  const { navData } = useNavigation();
  const tax = useTaxonomy();
  const [tab, setTab] = useState('review');
  const [summary, setSummary] = useState(null);
  const [defRows, setDefRows] = useState(null);
  const [run, setRun] = useState(null);
  const [error, setError] = useState('');
  const [tileFilter, setTileFilter] = useState(null);
  const wasRunning = useRef(false);

  const refreshSummary = useCallback(() => getClassificationSummary().then(setSummary).catch((e) => setError(apiError(e))), []);
  const loadDefs = useCallback(() => getDefinitions().then(setDefRows).catch(() => {}), []);
  useEffect(() => { refreshSummary(); loadDefs(); getClassifyRun().then(setRun).catch(() => {}); }, [refreshSummary, loadDefs]);

  const running = run?.status === 'running';
  useEffect(() => {
    if (!running) { if (wasRunning.current) { wasRunning.current = false; refreshSummary(); } return undefined; }
    wasRunning.current = true;
    const t = setInterval(() => getClassifyRun().then(setRun).catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [running, refreshSummary]);

  async function startRun() {
    setError('');
    try { await startClassifyRun(); setRun(await getClassifyRun()); } catch (e) { setError(apiError(e)); }
  }
  const defs = useMemo(() => Object.fromEntries((defRows || []).map((r) => [r.subcategory, r.definition])), [defRows]);
  const rv = summary?.review || {};

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1440px]">
      <style>{CATALOGUE_CSS}</style>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader title="Attributes">
          Each design&rsquo;s website category, sub-categories, occasions and attributes, filled from its scraped page first and ZOCS tags where the page has nothing. Check and fix them here.
        </PageHeader>
        <div className="flex items-center gap-3">
          {run && !running && <span className="text-[12px] text-gray-400">
            Last run {run.status === 'done' ? 'finished' : run.status} {ago(run.finished_at || run.started_at)}</span>}
          <SecondaryButton onClick={startRun} disabled={running}>
            <RefreshCw size={14} className={running ? 'motion-safe:animate-spin' : ''} /> {running ? 'Classifying…' : 'Re-run classification'}
          </SecondaryButton>
        </div>
      </div>
      {error && <Notice icon={CircleAlert} className="mt-4">{error}</Notice>}
      {run?.status === 'failed' && <Notice icon={CircleAlert} className="mt-4">The last run failed: {run.error}</Notice>}

      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Needs review" value={rv.needs_review} tone="text-amber-600" onClick={() => { setTab('review'); setTileFilter('needs_review'); }} />
        <Tile label="Mapped" value={rv.ok} tone="text-green-600" onClick={() => { setTab('review'); setTileFilter('ok'); }} />
        <Tile label="Approved" value={rv.approved} tone="text-sky-600" onClick={() => { setTab('review'); setTileFilter('approved'); }} />
        <Tile label="Not classified" value={rv.unclassified} tone="text-gray-500" onClick={() => { setTab('review'); setTileFilter('all'); }} />
      </div>

      <nav className="mt-6 flex gap-1 border-b border-gray-200" aria-label="Attributes sections">
        {[['review', 'Review'], ['unmapped', 'Unmapped words'], ['definitions', 'Definitions']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} aria-current={tab === k ? 'page' : undefined}
            className={`h-9 px-3.5 text-[13px] font-medium -mb-px border-b-2 transition ${tab === k ? 'border-zoddle-green text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'} ${focusRing} rounded-t`}>
            {l}
          </button>
        ))}
      </nav>

      <div className="mt-4">
        {tab === 'review' && <ReviewTab summary={summary} tax={tax} defs={defs} refreshSummary={refreshSummary}
          initialDesignId={navData?.designId} filterFromTile={tileFilter} setFilterFromTile={setTileFilter} />}
        {tab === 'unmapped' && <UnmappedTab onRun={startRun} running={running} />}
        {tab === 'definitions' && <DefinitionsTab rows={defRows} reload={loadDefs} />}
      </div>
    </div>
  );
}
